import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/client';
import PatentsTab from './compound-tabs/PatentsTab';
import TargetsTab from './compound-tabs/TargetsTab';
import PathwaysTab from './compound-tabs/PathwaysTab';
import ReactionsTab from './compound-tabs/ReactionsTab';
import BioactivityTab from './compound-tabs/BioactivityTab';
import ThreeDTab from './compound-tabs/ThreeDTab';
import { WorkbenchIcon } from './compound-tabs/WorkbenchTab';
import RegistrationTab from './compound-tabs/RegistrationTab';
import TargetUniverseTab from './compound-tabs/TargetUniverseTab';
import ADMETTab from './compound-tabs/ADMETTab';
import SARTab from './compound-tabs/SARTab';
import MetabolismTab from './compound-tabs/MetabolismTab';
import DesignTab from './compound-tabs/DesignTab';
import BatchesTab from './compound-tabs/BatchesTab';
import { StructureEditorModal } from './StructureEditor';
import StructureImage from './StructureImage';
import PpiComplexDialog from './PpiComplexDialog';
import { ZONE_COLORS, ZONE_LABELS } from '../../utils/patentZones';
import AssayResultsSection from '../assays/AssayResultsSection';
import SubmitForAssayModal from '../assays/SubmitForAssayModal';
import { ELN_API_BASE } from '../../config/externalUrls';
import { useUIStore } from '../../stores/uiStore';
import { getStoredToken } from '../../utils/tokenStorage';

export type DetailTab = 'workbench' | 'patents' | 'targets' | 'target-universe' | 'pathways' | 'reactions' | 'bioactivity' | '3d' | 'registration' | 'admet' | 'sar' | 'metabolism' | 'design' | 'batches' | 'complex';

interface CompoundDetailPanelProps {
  molecule: {
    id: number;
    smiles: string;
    molfile?: string;
    properties: Record<string, any>;
    mw?: number | null;
    logp?: number | null;
    tpsa?: number | null;
    hbd?: number | null;
    hba?: number | null;
    rotatable_bonds?: number | null;
    ring_count?: number | null;
    ip_status?: string | null;
    patent_max_zone?: number | null;
    inchi_key?: string | null;
    inchi?: string | null;
  } | null;
  activeTab?: DetailTab | null;
  onClose: () => void;
  onTabChange?: (tab: DetailTab | null) => void;
  isPinned?: boolean;
  onPinChange?: (pinned: boolean) => void;
  onPropertiesSaved?: () => void;
  projectId?: number;
}

const DATA_TABS: { id: DetailTab; label: string; icon?: React.ReactNode }[] = [
  { id: 'registration', label: 'Reg' },
  { id: 'workbench', label: 'Toolbox', icon: <WorkbenchIcon size={12} /> },
  { id: 'targets', label: 'Targets' },
  { id: 'complex', label: 'Complex' },
  { id: 'pathways', label: 'Pathways' },
  { id: 'reactions', label: 'Reactions' },
  { id: 'bioactivity', label: 'Bio' },
  { id: 'batches', label: 'Batches' },
  { id: 'patents', label: 'Patents' },
];

const ANALYSIS_TABS: { id: DetailTab; label: string; icon?: React.ReactNode }[] = [
  { id: 'admet', label: 'ADMET' },
  { id: 'sar', label: 'SAR' },
  { id: 'metabolism', label: 'Metab' },
  { id: 'target-universe', label: 'Universe' },
  { id: 'design', label: 'Design' },
];

const ALL_TABS = [...DATA_TABS, ...ANALYSIS_TABS];

const CompoundDetailPanel: React.FC<CompoundDetailPanelProps> = ({
  molecule,
  activeTab: controlledTab,
  onClose,
  onTabChange,
  isPinned = false,
  onPinChange,
  onPropertiesSaved,
  projectId,
}) => {
  const [internalTab, setInternalTab] = useState<DetailTab | null>(null);
  const [toolboxCopied, setToolboxCopied] = useState(false);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [svgLoading, setSvgLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [assayModalOpen, setAssayModalOpen] = useState(false);
  const [assayResultsKey, setAssayResultsKey] = useState(0);
  const [elnLoading, setElnLoading] = useState(false);
  const showNotification = useUIStore((s) => s.showNotification);

  const activeTab = controlledTab !== undefined ? controlledTab : internalTab;

  const handlePopout = useCallback(() => {
    if (!molecule) return;
    const url = `/molecule/${molecule.id}?popout=true`;
    window.open(url, `discoverant-molecule-${molecule.id}`, 'width=500,height=750,resizable=yes,scrollbars=yes');
  }, [molecule]);

  const handleTabChange = useCallback((tab: DetailTab | null) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalTab(tab);
    }
  }, [onTabChange]);

  const handleToolboxClick = useCallback(() => {
    if (!molecule) return;
    navigator.clipboard.writeText(molecule.smiles).then(() => {
      setToolboxCopied(true);
      setTimeout(() => setToolboxCopied(false), 2000);
    });
    const params = new URLSearchParams({ smiles: molecule.smiles, popout: 'true' });
    if (projectId != null) params.set('project_id', String(projectId));
    window.open(`/hubs?${params.toString()}`, '_blank');
  }, [molecule, projectId]);

  const handleLogToEln = useCallback(async () => {
    if (!molecule || elnLoading) return;

    // Open the destination synchronously from the user's click so browsers do
    // not block it after the asynchronous ELN record creation request.
    const elnWindow = window.open('about:blank', '_blank');
    if (!elnWindow) {
      showNotification('error', 'Please allow pop-ups for this site to open the ELN in a new tab');
      return;
    }
    elnWindow.opener = null;
    elnWindow.document.title = 'Opening ELN…';
    elnWindow.document.body.innerHTML = '<p style="font: 16px system-ui; color: #334155; padding: 24px;">Creating the ELN record…</p>';

    setElnLoading(true);
    try {
      const token = getStoredToken();
      if (!token) throw new Error('Please sign in again before logging to ELN');
      const resp = await fetch(`${ELN_API_BASE}/api/chemistry/reactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reaction_smiles: molecule.smiles,
          target_compound: getName(),
          study_id: projectId ? String(projectId) : 'untagged',
          compound_id: String(molecule.id),
        }),
      });
      if (!resp.ok) throw new Error(`ELN HTTP ${resp.status}`);
      const data = await resp.json();
      if (!data?.record_id) throw new Error('ELN response missing record_id');
      showNotification('success', `Logged to ELN ✓ (${data.record_id})`);
      // Open the ELN at the just-logged record (Records tab, detail open).
      elnWindow.location.replace(`/workbench-api/eln/?record_id=${encodeURIComponent(data.record_id)}`);
    } catch (err: any) {
      elnWindow.close();
      showNotification('error', err?.message?.slice(0, 120) || 'Failed to log to ELN');
    } finally {
      setElnLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [molecule, elnLoading, projectId]);

  // Fetch structure SVG
  useEffect(() => {
    if (!molecule?.smiles) {
      setSvgContent(null);
      return;
    }

    const fetchSvg = async () => {
      setSvgLoading(true);
      try {
        const response = await apiClient.post('/v2/indigo/render', {
          structure: { value: molecule.smiles, type: 'RAW' },
          width: '360px',
          height: '180px',
        });
        const svg = typeof response.data === 'string' ? response.data : response.data.svg || response.data;
        setSvgContent(svg);
      } catch {
        setSvgContent(null);
      } finally {
        setSvgLoading(false);
      }
    };

    fetchSvg();
  }, [molecule?.smiles]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Reset tab when molecule changes
  useEffect(() => {
    if (!controlledTab) {
      setInternalTab(null);
    }
  }, [molecule?.id, controlledTab]);

  const isOpen = molecule !== null;

  // Get display name
  const getName = (): string => {
    if (!molecule) return '';
    const props = molecule.properties || {};
    for (const key of ['Name', 'name', 'Molecule', 'Compound', 'Title', 'ID']) {
      if (props[key] && String(props[key]).trim()) return String(props[key]).trim();
    }
    const smiles = molecule.smiles || '';
    return smiles.length > 30 ? `${smiles.substring(0, 30)}...` : smiles;
  };

  // Get patent zone info
  const getZoneInfo = () => {
    if (!molecule) return null;
    const zone = molecule.patent_max_zone ?? molecule.properties?.patent_max_zone;
    if (zone == null) return null;
    return {
      zone,
      color: ZONE_COLORS[zone] || '#6b7280',
      label: ZONE_LABELS[zone] || `Zone ${zone}`,
    };
  };

  const mw = molecule ? (molecule.mw ?? molecule.properties?.MW) : null;
  const zoneInfo = getZoneInfo();

  return (
    <div
      className="flex-shrink-0 border-l flex flex-col overflow-hidden h-full"
      style={{
        backgroundColor: 'var(--card-bg)',
        borderColor: 'var(--card-border)',
        width: isOpen ? 600 : 0,
        minWidth: isOpen ? 600 : 0,
        transition: 'width 300ms ease, min-width 300ms ease',
      }}
    >
      {molecule && (
        <>
          {/* Header */}
          <div className="flex-shrink-0 border-b" style={{ borderColor: 'var(--card-border)' }}>
            {/* Title row + close */}
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
              <h3 className="text-sm font-medium truncate max-w-[280px]" style={{ color: 'var(--text-primary)' }} title={getName()}>
                {getName()}
              </h3>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                {/* Pin button */}
                <button
                  onClick={() => onPinChange?.(!isPinned)}
                  className="p-1 rounded transition-all flex-shrink-0"
                  style={{ color: isPinned ? '#0d9488' : 'var(--text-secondary)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  title={isPinned ? 'Unpin — panel will close when clicking away' : 'Pin — keep panel open while navigating'}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ transform: isPinned ? 'rotate(0deg)' : 'rotate(45deg)', transition: 'transform 200ms ease' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                  </svg>
                </button>
                {/* Popout button */}
                <button
                  onClick={handlePopout}
                  className="p-1 rounded transition-colors flex-shrink-0"
                  style={{ color: 'var(--text-secondary)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                  title="Pop out to separate window"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </button>
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="p-1 rounded transition-colors flex-shrink-0"
                  style={{ color: 'var(--text-secondary)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--hover-bg)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                  title="Close (Esc)"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Structure image */}
            <div className="px-4 pb-2">
              {/* TODO: Double-clicking the structure area should open Ketcher with the full editor */}
              <div
                className="rounded-lg overflow-hidden flex items-center justify-center"
                style={{ height: 160, backgroundColor: 'var(--content-bg)' }}
              >
                {svgLoading ? (
                  <div className="w-6 h-6 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin" />
                ) : svgContent ? (
                  <div
                    className="w-full h-full flex items-center justify-center cursor-pointer transition-colors"
                    style={{ backgroundColor: 'var(--content-bg)' }}
                    dangerouslySetInnerHTML={{ __html: svgContent }}
                    onClick={() => setEditorOpen(true)}
                    title="Click to edit in Ketcher"
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--content-bg)'; }}
                  />
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    {molecule.smiles ? (
                      <StructureImage smiles={molecule.smiles} moleculeId={molecule.id ? Number(molecule.id) : undefined} size={150} />
                    ) : (
                      <>
                        <svg className="w-8 h-8" style={{ color: 'var(--text-muted)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
                        </svg>
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>No structure</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* SMILES string */}
            {molecule.smiles && (
              <div className="px-4 pb-1">
                <span
                  className="text-[10px] font-mono block truncate cursor-pointer"
                  style={{ color: 'var(--text-muted)' }}
                  title={`Click to copy: ${molecule.smiles}`}
                  onClick={() => navigator.clipboard.writeText(molecule.smiles)}
                >
                  {molecule.smiles}
                </span>
              </div>
            )}

            {/* Quick stats row: MW (bold), patent zone indicator */}
            <div className="px-4 pb-2 flex items-center gap-3 flex-wrap">
              {mw != null && (
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  MW{' '}
                  <span className="font-mono font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                    {Math.round(mw * 100) / 100}
                  </span>
                </span>
              )}
              {(molecule.logp ?? molecule.properties?.LogP) != null && (
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  LogP <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{Math.round((molecule.logp ?? molecule.properties?.LogP) * 100) / 100}</span>
                </span>
              )}
              {(molecule.tpsa ?? molecule.properties?.TPSA) != null && (
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  TPSA <span className="font-mono" style={{ color: 'var(--text-primary)' }}>{Math.round((molecule.tpsa ?? molecule.properties?.TPSA) * 100) / 100}</span>
                </span>
              )}
              {/* Patent zone indicator */}
              {zoneInfo && (
                <span className="flex items-center gap-1 text-xs ml-auto">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: zoneInfo.color }}
                  />
                  <span style={{ color: 'var(--text-secondary)' }}>{zoneInfo.label}</span>
                </span>
              )}
            </div>

            {/* Submit for Assay button + Log to ELN button + Assay results */}
            <div className="px-4 pb-2 space-y-1.5">
              <button
                onClick={() => setAssayModalOpen(true)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md"
                style={{
                  color: '#0D9488',
                  border: '1px solid rgba(13,148,136,0.3)',
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(13,148,136,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                title="Submit this compound for biological assay"
              >
                {/* Beaker icon */}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 3h6M10 3v6.5L4 20h16L14 9.5V3" />
                </svg>
                Submit for Assay
              </button>
              <button
                onClick={handleLogToEln}
                disabled={elnLoading}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md"
                style={{
                  color: '#0D9488',
                  border: '1px solid rgba(13,148,136,0.3)',
                  backgroundColor: 'transparent',
                  opacity: elnLoading ? 0.6 : 1,
                  cursor: elnLoading ? 'wait' : 'pointer',
                }}
                onMouseEnter={(e) => { if (!elnLoading) e.currentTarget.style.backgroundColor = 'rgba(13,148,136,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                title="Create a chemistry record in ELN and open it in a new tab"
              >
                {/* Clipboard icon */}
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                  <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                  <path d="M8 11h8M8 15h6" />
                </svg>
                {elnLoading ? 'Logging…' : 'Log to ELN'}
              </button>
              <AssayResultsSection key={assayResultsKey} moleculeId={molecule.id} />
            </div>

            {/* Context label */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '0 8px', marginBottom: 2 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              <span style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', color: 'var(--text-muted)', textTransform: 'uppercase' as const }}>
                Project Intelligence
              </span>
            </div>

            {/* Tab bar — two rows, wrapping */}
            <div className="px-2">
              {/* Row 1: Data tabs */}
              <div className="flex gap-0.5 flex-wrap" style={{ scrollbarWidth: 'none' }}>
                {DATA_TABS.map((tab) => {
                  const isToolbox = tab.id === 'workbench';
                  const isActive = !isToolbox && activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => (isToolbox ? handleToolboxClick() : handleTabChange(tab.id))}
                      className="px-2 py-1.5 text-xs whitespace-nowrap transition-colors flex items-center gap-1"
                      style={{
                        color: isActive ? '#0D9488' : 'var(--text-secondary)',
                        borderBottom: isActive ? '2px solid #0D9488' : '2px solid transparent',
                        backgroundColor: isActive ? 'var(--hover-bg)' : 'transparent',
                        fontWeight: isActive ? 600 : 400,
                      }}
                      title={isToolbox ? 'Copy SMILES + open Toolbox in new tab' : undefined}
                    >
                      {tab.icon}{isToolbox && toolboxCopied ? 'Copied!' : tab.label}
                    </button>
                  );
                })}
              </div>
              {/* Row 2: Analysis tabs */}
              <div className="flex gap-0.5 flex-wrap" style={{ scrollbarWidth: 'none' }}>
                {ANALYSIS_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className="px-2 py-1.5 text-xs whitespace-nowrap transition-colors flex items-center gap-1"
                    style={{
                      color: activeTab === tab.id ? '#0D9488' : 'var(--text-secondary)',
                      borderBottom: activeTab === tab.id ? '2px solid #0D9488' : '2px solid transparent',
                      backgroundColor: activeTab === tab.id ? 'var(--hover-bg)' : 'transparent',
                      fontWeight: activeTab === tab.id ? 600 : 400,
                    }}
                  >
                    {tab.icon}{tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab content */}
          {activeTab === 'complex' && <PpiComplexDialog key={molecule.id} molecule={molecule}
            onClose={() => handleTabChange(null)} onShowResults={() => handleTabChange('bioactivity')} />}
          <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'target-universe' && <TargetUniverseTab smiles={molecule.smiles} moleculeId={molecule.id} />}
            {activeTab === 'targets' && <TargetsTab smiles={molecule.smiles} moleculeId={molecule.id} />}
            {activeTab === 'pathways' && <PathwaysTab smiles={molecule.smiles} />}
            {activeTab === 'reactions' && <ReactionsTab smiles={molecule.smiles} inchikey={molecule.inchi_key} projectId={projectId} />}
            {activeTab === 'bioactivity' && <BioactivityTab moleculeId={molecule.id} smiles={molecule.smiles} />}
            {activeTab === 'batches' && <BatchesTab smiles={molecule.smiles} inchiKey={molecule.inchi_key} projectId={projectId} />}
            {activeTab === 'patents' && <PatentsTab smiles={molecule.smiles} enrichedZone={molecule.patent_max_zone ?? molecule.properties?.patent_max_zone} />}
            {activeTab === 'registration' && <RegistrationTab smiles={molecule.smiles} molfile={molecule.molfile || (molecule as any).molblock} />}
            {activeTab === 'admet' && <ADMETTab smiles={molecule.smiles} />}
            {activeTab === 'sar' && <SARTab smiles={molecule.smiles} />}
            {activeTab === 'metabolism' && <MetabolismTab smiles={molecule.smiles} />}
            {activeTab === 'design' && <DesignTab smiles={molecule.smiles} moleculeId={molecule.id} />}
          </div>

          <StructureEditorModal
            isOpen={editorOpen}
            onClose={() => setEditorOpen(false)}
            initialStructure={molecule.molfile || molecule.smiles || ''}
            title={getName() || 'View Structure'}
          />

          <SubmitForAssayModal
            isOpen={assayModalOpen}
            onClose={() => setAssayModalOpen(false)}
            moleculeId={molecule.id}
            projectId={projectId || 0}
            onSubmitted={() => setAssayResultsKey((k) => k + 1)}
          />
        </>
      )}
    </div>
  );
};

export default CompoundDetailPanel;
